/**
 * /decision-ledger — Engine 24 · Decision Ledger.
 *
 * Every money decision = a card with TWO futures, a human approval gate, and
 * a predicted-vs-actual accuracy grade. We grade our own forecasts — no other
 * tool dares.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, RefreshCw, AlertCircle, Check, X, Target, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

const usd = (v) => (v >= 0 ? `+$${Math.abs(v).toLocaleString()}` : `−$${Math.abs(v).toLocaleString()}`);

const Stat = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl p-5" data-testid={testId}>
    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
    <div className={`text-3xl font-extrabold tabular-nums font-mono ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Field = ({ label, children }) => (
  <label className="text-xs text-slate-400 flex flex-col gap-1">{label}{children}</label>
);
const inputCls = "rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100 focus:outline-none focus:border-amber-400/50";

const STATUS_STYLES = {
  pending: 'bg-amber-500/10 border-amber-400/40 text-amber-300',
  approved: 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300',
  rejected: 'bg-rose-500/10 border-rose-400/40 text-rose-300',
};

const DecisionCard = ({ d, onResolve, onOutcome }) => {
  const [actual, setActual] = useState('');
  const rec = d.recommended_option === 'a' ? d.option_a : d.option_b;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl p-5" data-testid={`dl-card-${d.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold text-white">{d.title}</div>
          {d.reason && <div className="text-xs text-slate-400 mt-1 max-w-xl">{d.reason}</div>}
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border ${STATUS_STYLES[d.status]}`} data-testid={`dl-status-${d.id}`}>{d.status}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {[['a', d.option_a], ['b', d.option_b]].map(([k, opt]) => (
          <div key={k} className={`rounded-lg border p-3 ${d.recommended_option === k ? 'border-emerald-400/40 bg-emerald-500/5' : 'border-white/10 bg-slate-950/40'}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Option {k.toUpperCase()} {d.recommended_option === k && <span className="text-emerald-400 normal-case font-semibold">· recommended</span>}
            </div>
            <div className="text-sm text-slate-200">{opt.label}</div>
            <div className={`text-lg font-extrabold tabular-nums mt-1 ${opt.projected_monthly_delta_usd >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {usd(opt.projected_monthly_delta_usd)}/mo
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {d.status === 'pending' && (
          <>
            <button onClick={() => onResolve(d.id, 'approved')} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold" data-testid={`dl-approve-${d.id}`}><Check className="w-3.5 h-3.5" /> Approve</button>
            <button onClick={() => onResolve(d.id, 'rejected')} className="inline-flex items-center gap-1 rounded-lg bg-rose-500/80 hover:bg-rose-600 text-white px-3 py-1.5 text-xs font-bold" data-testid={`dl-reject-${d.id}`}><X className="w-3.5 h-3.5" /> Reject</button>
            <span className="text-[11px] text-slate-500">Human-in-the-loop: nothing executes without your call.</span>
          </>
        )}
        {d.status === 'approved' && d.forecast_accuracy_pct == null && (
          <div className="flex items-center gap-2">
            <input type="number" placeholder="Actual $/mo" value={actual} onChange={(e) => setActual(e.target.value)} className={`${inputCls} w-32`} data-testid={`dl-actual-input-${d.id}`} />
            <button onClick={() => actual !== '' && onOutcome(d.id, Number(actual))} className="inline-flex items-center gap-1 rounded-lg bg-sky-500 hover:bg-sky-600 text-white px-3 py-1.5 text-xs font-bold" data-testid={`dl-outcome-btn-${d.id}`}><Target className="w-3.5 h-3.5" /> Record actual</button>
          </div>
        )}
        {d.forecast_accuracy_pct != null && (
          <span className="text-xs font-bold text-sky-300" data-testid={`dl-accuracy-${d.id}`}>
            Forecast graded: {d.forecast_accuracy_pct}% accurate (predicted {usd(d.projected_monthly_delta_usd)}/mo, actual {usd(d.actual_monthly_delta_usd)}/mo)
          </span>
        )}
        {d.decided_by && <span className="text-[11px] text-slate-600 ml-auto">decided by {d.decided_by}</span>}
      </div>
    </div>
  );
};

const DecisionLedger = () => {
  const [acc, setAcc] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [unauth, setUnauth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', reason: '', aLabel: '', aUsd: '', bLabel: 'Status quo — do nothing', bUsd: '0' });
  const [formErr, setFormErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [a, d] = await Promise.all([
        fetch(`${API}/decision-ledger/accuracy`, { headers: auth() }),
        fetch(`${API}/decision-ledger/decisions`, { headers: auth() }),
      ]);
      if (!a.ok || !d.ok) throw new Error('unauthorized');
      setAcc(await a.json());
      setDecisions((await d.json()).decisions);
      setUnauth(false);
    } catch (e) {
      setUnauth(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.title || form.aLabel === '' || form.aUsd === '') { setFormErr('Title, Option A label and $/mo are required.'); return; }
    setFormErr('');
    const r = await fetch(`${API}/decision-ledger/decisions`, {
      method: 'POST', headers: auth(),
      body: JSON.stringify({
        title: form.title, reason: form.reason || null,
        option_a: { label: form.aLabel, projected_monthly_delta_usd: Number(form.aUsd) || 0 },
        option_b: { label: form.bLabel || 'Status quo', projected_monthly_delta_usd: Number(form.bUsd) || 0 },
      }),
    });
    if (r.ok) {
      setForm({ title: '', reason: '', aLabel: '', aUsd: '', bLabel: 'Status quo — do nothing', bUsd: '0' });
      setShowForm(false);
      load();
    } else {
      setFormErr((await r.json())?.detail || 'Failed to create decision');
    }
  };

  const resolve = async (id, action) => {
    await fetch(`${API}/decision-ledger/decisions/${id}/resolve`, { method: 'POST', headers: auth(), body: JSON.stringify({ action }) });
    load();
  };
  const outcome = async (id, actual) => {
    await fetch(`${API}/decision-ledger/decisions/${id}/outcome`, { method: 'POST', headers: auth(), body: JSON.stringify({ actual_monthly_delta_usd: actual }) });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="decision-ledger-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Badge className="bg-amber-500/10 border-amber-400/40 text-amber-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="dl-badge">
            <Scale className="w-3 h-3 mr-1.5 inline" /> Engine 24 · Decision Ledger · human-in-the-loop
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Decision Ledger</BrandTitle></h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            Every FinOps tool recommends <em>after</em> the money is spent. This ledger stands at the decision moment:
            two futures side-by-side, a reason, a human approval gate — and then we
            <span className="text-amber-300 font-semibold"> grade our own prediction against reality</span>. No other tool dares.
          </p>
        </div>

        {unauth ? (
          <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl px-6 py-12 text-center text-slate-500" data-testid="dl-unauth">
            <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            Sign in to open your Decision Ledger. Try it instantly with the <span className="text-emerald-400">Live Demo</span> button in the navbar.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Stat label="Decisions tracked" value={acc?.decisions_tracked ?? '—'} sub={`${acc?.pending ?? 0} awaiting your call`} testId="dl-stat-tracked" />
              <Stat label="Forecast accuracy" value={acc?.avg_forecast_accuracy_pct != null ? `${acc.avg_forecast_accuracy_pct}%` : '—'} color="text-sky-300" sub={`${acc?.outcomes_graded ?? 0} outcomes graded`} testId="dl-stat-accuracy" />
              <Stat label="Approved impact" value={acc ? usd(acc.total_projected_monthly_usd) : '—'} color="text-emerald-300" sub="projected $/mo across approvals" testId="dl-stat-projected" />
              <Stat label="Actual impact" value={acc ? usd(acc.total_actual_monthly_usd) : '—'} color="text-emerald-300" sub="measured $/mo on graded outcomes" testId="dl-stat-actual" />
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-white">Decision cards</h2>
              <div className="flex gap-2">
                <button onClick={load} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 text-sm font-semibold" data-testid="dl-refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
                <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 px-3 py-1.5 text-sm font-bold" data-testid="dl-new-btn">
                  <Plus className="w-3.5 h-3.5" /> New decision
                </button>
              </div>
            </div>

            {showForm && (
              <Card className="bg-slate-900/60 border-amber-400/20 mb-6">
                <CardContent className="p-5">
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <Field label="Decision title *"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Swap support-bot from GPT-4o to Haiku" data-testid="dl-form-title" /></Field>
                    <Field label="Reason / context"><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why this decision is on the table" data-testid="dl-form-reason" /></Field>
                    <Field label="Option A label *"><input className={inputCls} value={form.aLabel} onChange={(e) => setForm({ ...form, aLabel: e.target.value })} placeholder="Make the change" data-testid="dl-form-a-label" /></Field>
                    <Field label="Option A projected $/mo * (+saves / −loses)"><input type="number" className={inputCls} value={form.aUsd} onChange={(e) => setForm({ ...form, aUsd: e.target.value })} placeholder="4200" data-testid="dl-form-a-usd" /></Field>
                    <Field label="Option B label"><input className={inputCls} value={form.bLabel} onChange={(e) => setForm({ ...form, bLabel: e.target.value })} data-testid="dl-form-b-label" /></Field>
                    <Field label="Option B projected $/mo"><input type="number" className={inputCls} value={form.bUsd} onChange={(e) => setForm({ ...form, bUsd: e.target.value })} data-testid="dl-form-b-usd" /></Field>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={create} className="rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-1.5 text-sm font-bold" data-testid="dl-form-submit">Create card</button>
                    {formErr && <span className="text-xs text-rose-400" data-testid="dl-form-error">{formErr}</span>}
                    <span className="text-[11px] text-slate-500">Tip: the <Link to="/decision-simulator" className="text-emerald-400 hover:underline">Decision Simulator</Link> can compute projections for 6 common scenario types.</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4" data-testid="dl-cards-list">
              {decisions.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl px-6 py-10 text-center text-slate-500" data-testid="dl-empty">
                  <Scale className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  No decisions yet. Create your first card — before the money moves, not after.
                </div>
              ) : (
                decisions.map((d) => <DecisionCard key={d.id} d={d} onResolve={resolve} onOutcome={outcome} />)
              )}
            </div>
          </>
        )}

        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mt-10">
          <CardContent className="p-5">
            <h3 className="font-bold text-white mb-2">Why grading our own forecasts matters</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              "Recommendations" are free to make and never audited — that's why engineering teams ignore them.
              A prediction with a public accuracy grade is a different animal: it's accountable. Every decision here
              records who approved it, why, what we predicted, and what actually happened. Over time your ledger
              becomes the one document a CFO trusts more than any dashboard: a track record.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">First-to-market: as of June 2026 no FinOps tool tracks predicted-vs-actual accuracy per decision with a human-in-the-loop approval trail.</p>
            <Link to="/forecasts-graded" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 mt-3 font-semibold" data-testid="dl-link-public-grades">
              See our aggregate accuracy, published in public <Target className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DecisionLedger;
